import React from 'react';
import { sortBy } from 'lodash';
import { useDispatch } from 'react-redux';
import { withFormsy } from 'formsy-react';
import {
	Box,
	Chip,
	CircularProgress,
	InputAdornment,
	ListItemText,
	styled,
	TextField,
	type CreateFilterOptionsConfig
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useDeepCompareEffect } from '@fuse/hooks';

import { type FilterItemBaseProps } from '../FilterItemManager';
import { executeStored } from 'app/main/components/Utils';
import useSettings from 'app/main/components/hooks/useSettings';

type ListFilterItemProps = {
	/** The system table name */
	systable: string;
	/** Function used to determine the string value for a given option */
	getOptionLabel?: (option: any) => string;
	/** Function that determines the options that are eligible */
	filterOptions?: CreateFilterOptionsConfig<any>;
	/** Function that group items by the specified criterium */
	groupBy?: (option: any) => string;
} & FilterItemBaseProps<string[] | number[]>;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	'& > .MuiTextField-root > .MuiInputBase-root > .MuiInputBase-input': {
		paddingTop: 6,
		paddingBottom: 6
	}
}));

function ListFilterItem(props: ListFilterItemProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(props.systable);

	const idName = `${settings.modu_short}_id`;
	const isDisabled = (props.defaultValue?.length || 0) > 0;

	const [options, setOptions] = React.useState<any[]>();
	const [isLoading, setIsLoading] = React.useState(false);
	const [open, setOpen] = React.useState(false);

	const selectedOptions = options?.filter(option => props.value?.find(v => v === option[idName])) || [];

	const getTitle = option => option[`${settings.modu_short}_title`];
	const parseOptionLabel = props.getOptionLabel || getTitle;

	function handleChange(_event, selectedValue) {
		if (selectedValue) {
			const values = selectedValue.map(v => v[idName]);
			props.sendChange([...values]);
		} else {
			props.sendChange();
		}
	}

	function renderOption({ key, ...optionProps }: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: any) {
		return (
			<Box key={key} component="li" {...optionProps}>
				<ListItemText
					primary={parseOptionLabel(option)}
					secondary={option[`${settings.modu_short}_description`]}
				/>
			</Box>
		);
	}

	function renderValue(fieldValue: any | any[], getItemProps) {
		return fieldValue.map((option, index) => {
			const { key, ...itemProps } = getItemProps({ index });
			return (
				<Chip
					key={key}
					{...itemProps}
					label={parseOptionLabel(option)}
					size="small"
					disabled={itemProps.disabled}
					onDelete={itemProps.onDelete}
				/>
			);
		});
	}

	useDeepCompareEffect(() => {
		if (!options) {
			setIsLoading(true);
			executeStored({
				action: 'st_sys_systable_list',
				params: { systable: props.systable },
				dispatch,
				successHandler: response => {
					let { list } = response.data;
					if (props.groupBy) list = sortBy(list, props.groupBy);
					setOptions(list || []);
					setIsLoading(false);
				}
			});
		}
	}, [dispatch, options, props.groupBy, props.systable]);

	const sortedOptions = props.getOptionLabel ? sortBy(options, props.getOptionLabel) : options;

	return (
		<StyledAutocomplete
			className={props.className}
			renderInput={params => (
				<TextField
					{...params}
					variant="outlined"
					size="small"
					fullWidth
					autoComplete="off"
					placeholder={props.placeholder}
					InputLabelProps={{
						...params.InputLabelProps,
						shrink: true
					}}
					InputProps={{
						...params.InputProps,
						endAdornment: !isDisabled && (
							<InputAdornment position="end">
								{isLoading ? <CircularProgress color="inherit" size={20} disableShrink /> : null}
								{params.InputProps.endAdornment}
							</InputAdornment>
						)
					}}
				/>
			)}
			autoComplete={false}
			options={sortedOptions || []}
			value={selectedOptions}
			onChange={handleChange}
			disabled={isDisabled}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			getOptionLabel={parseOptionLabel}
			renderOption={renderOption}
			renderValue={renderValue}
			isOptionEqualToValue={(option: any, fieldValue: any) => option[idName] === fieldValue[idName]}
			filterOptions={createFilterOptions(props.filterOptions)}
			groupBy={props.groupBy}
			multiple
			disableCloseOnSelect
			autoHighlight
			selectOnFocus={false}
		/>
	);
}

export default React.memo(withFormsy(ListFilterItem));
